import { useCallback, useEffect, useRef, useState } from 'react';
import { createCallRecorder, recordingSupported, createRecordingAudioContext } from '../utils/callRecording';

export default function useCallRecording(call, localStream, remoteStream) {
  const [state, setState] = useState('idle');
  const [incoming, setIncoming] = useState(null);
  const [peerRecording, setPeerRecording] = useState(false);
  const [error, setError] = useState('');
  const [download, setDownload] = useState(null);
  const session = useRef(null);
  const url = useRef(null);
  const current = useRef({ call, localStream, remoteStream });
  current.current = { call, localStream, remoteStream };
  const send = (s, type, id) => {
    if (s.channel?.readyState !== 'open') return false;
    try { s.channel.send(JSON.stringify({ type, id, callId: s.id })); return true; } catch { return false; }
  };
  const stop = useCallback(() => {
    const s = session.current;
    if (!s) return;
    clearTimeout(s.timer);
    if (s.request) send(s, 'record-stop', s.request);
    s.request = null;
    s.audioContext?.close().catch(() => {}); s.audioContext = null;
    s.recorder?.stop(); s.recorder = null;
    setState('idle');
  }, []);
  useEffect(() => {
    const channel = call?.recordingChannel;
    const s = { id: call?.id, channel, request: null, incoming: null, allowed: null, recorder: null };
    session.current = s;
    setState('idle'); setIncoming(null); setPeerRecording(false); setError('');
    const receive = event => {
      let message;
      try { if (typeof event.data !== 'string' || event.data.length > 4096) return; message = JSON.parse(event.data); } catch { return; }
      if (message.callId !== s.id || typeof message.id !== 'string' || message.id.length > 100) return;
      const { type, id } = message;
      if (type === 'record-request') {
        if (s.request || s.incoming || s.allowed) { send(s, 'record-decline', id); return; }
        s.incoming = id; setIncoming(id);
        s.incomingTimer = setTimeout(() => {
          if (s.incoming === id) { send(s, 'record-decline', id); s.incoming = null; setIncoming(null); }
        }, 30000);
      } else if (type === 'record-allow' && s.request === id && !s.recorder) {
        clearTimeout(s.timer);
        const latest = current.current;
        if (latest.call?.id !== s.id || latest.call?.connectionState !== 'connected') { stop(); return; }
        try {
          s.recorder = createCallRecorder({ ...latest, audioContext: s.audioContext, video: latest.call.type === 'video',
            onComplete: blob => {
              if (url.current) URL.revokeObjectURL(url.current);
              url.current = URL.createObjectURL(blob);
              const extension = blob.type.includes('mp4') ? 'mp4' : 'webm';
              setDownload({ url: url.current, name: `CCUK-call-${new Date().toISOString().replace(/[:.]/g,'-')}.${extension}` });
            }, onError: message => { if (session.current === s) { setError(message); stop(); } },
          });
          s.audioContext = null;
          if (!send(s, 'record-start', id)) { stop(); setError('Recording stopped because the call controls disconnected.'); return; }
          setState('recording');
          // Keep memory bounded for local browser recordings.
          s.timer = setTimeout(stop, 15 * 60 * 1000);
        } catch (err) { setError(err.message || 'Recording could not start.'); stop(); }
      } else if (type === 'record-decline' && s.request === id) {
        stop(); setError('Recording was not approved.');
      } else if (type === 'record-start' && s.allowed === id) {
        setPeerRecording(true);
      } else if (type === 'record-stop') {
        if (s.incoming === id) { s.incoming = null; setIncoming(null); clearTimeout(s.incomingTimer); }
        if (s.allowed === id) { s.allowed = null; setPeerRecording(false); }
        if (s.request === id) stop();
      }
    };
    const close = () => { stop(); s.allowed = null; s.incoming = null; setIncoming(null); setPeerRecording(false); };
    channel?.addEventListener('message', receive);
    channel?.addEventListener('close', close);
    return () => {
      clearTimeout(s.timer); clearTimeout(s.incomingTimer);
      if (s.request) send(s, 'record-stop', s.request);
      if (s.allowed) send(s, 'record-stop', s.allowed);
      s.recorder?.stop();
      s.audioContext?.close().catch(() => {}); s.audioContext = null;
      channel?.removeEventListener('message', receive);
      channel?.removeEventListener('close', close);
    };
  }, [call?.id, call?.recordingChannel, stop]);
  useEffect(() => () => { if (url.current) URL.revokeObjectURL(url.current); }, []);
  useEffect(() => {
    if (call?.connectionState && call.connectionState !== 'connected') stop();
  }, [call?.connectionState, stop]);
  const request = async () => {
    const s = session.current;
    if (!s || s.request || s.incoming || s.allowed || call?.connectionState !== 'connected') return;
    setError('');
    if (!recordingSupported(call.type === 'video')) { setError('Local recording is not supported in this browser.'); return; }
    if (download) { setError('Save or dismiss your previous recording before starting another.'); return; }
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    s.request = id; setState('waiting');
    s.timer = setTimeout(() => { stop(); setError('Recording permission request expired.'); }, 30000);
    try {
      // Unlock audio in the initiating user's click, before awaiting consent.
      // No call streams are connected or captured at this point.
      s.audioContext = createRecordingAudioContext();
      await s.audioContext.resume();
      if (session.current !== s || s.request !== id) return;
      if (!send(s, 'record-request', id)) { stop(); setError('Recording requires both participants to use the updated CCUK call window.'); }
    } catch { if (session.current === s && s.request === id) { stop(); setError('Recording audio could not be enabled. Try again in a supported browser.'); } }

  };
  const respond = allow => {
    const s = session.current;
    if (!s?.incoming) return;
    clearTimeout(s.incomingTimer);
    if (allow && send(s, 'record-allow', s.incoming)) s.allowed = s.incoming;
    else send(s, 'record-decline', s.incoming);
    s.incoming = null; setIncoming(null);
  };
  const revoke = () => {
    const s = session.current;
    if (s?.allowed) send(s, 'record-stop', s.allowed);
    if (s) s.allowed = null;
    setPeerRecording(false);
  };
  const dismiss = () => { if (url.current) URL.revokeObjectURL(url.current); url.current = null; setDownload(null); };
  return { state, incoming, peerRecording, error, download, request, stop, respond, revoke, dismiss };
}
